import { useState, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { AlignCenter, AlignLeft, AlignRight, Bold, CaseSensitive, ChevronDown, Italic, Minus, Plus, RotateCcw, Search, Underline, X } from "lucide-react";
import { TEXT_SIZES, TEXT_SIZE_SCALE, TEXT_WEIGHTS, type SectionNode, type TextStyle, type WebsiteDefinition } from "@baze/schema";
import { fontStack } from "@baze/engine";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip } from "@/components/ui/tooltip";
import { t } from "@/i18n/id";
import { cn } from "@/lib/cn";
import type { themeFor } from "@/lib/catalog";
import { locateSection, updateTextStyle } from "../model/ops";
import { ColorField } from "./ColorField";
import { siteSwatches } from "./StylePanel";

type Apply = (next: WebsiteDefinition, key?: string) => void;
type Theme = NonNullable<ReturnType<typeof themeFor>>;
type Size = (typeof TEXT_SIZES)[number];

export interface TextTarget {
  sectionId: string;
  path: string;
}

/** "content.items.2.name" → "content.items.*.name": the style key for every item of a list. */
export const genericKey = (path: string) => path.replace(/\.\d+(?=\.|$)/g, ".*");

/** The style a text shows: the every-item style, then its own on top. */
export function effectiveTextStyle(section: SectionNode | undefined, path: string): TextStyle {
  const texts = section?.style?.texts ?? {};
  return { ...(texts[genericKey(path)] ?? {}), ...(texts[path] ?? {}) };
}

/** Human name of a text from its path ("content.items.2.name" → "Nama · item 3"). */
export function textLabel(path: string): string {
  const segs = path.split(".").slice(1);
  const names = t.editor.textStyle.fieldNames as Record<string, string>;
  const last = [...segs].reverse().find((s) => !/^\d+$|^\*$/.test(s)) ?? segs.at(-1) ?? path;
  const name = names[last] ?? last;
  const index = segs.find((s) => /^\d+$/.test(s));
  return segs.includes("*") ? `${name} · ${t.editor.textStyle.allItems.toLowerCase()}` : index !== undefined ? `${name} · item ${Number(index) + 1}` : name;
}

const ALIGN_ICONS = { start: AlignLeft, center: AlignCenter, end: AlignRight } as const;
const ALIGN_NEXT = { start: "center", center: "end", end: "start" } as const;

/**
 * Style controls for the text being edited: font, size, weight, italic, underline, letter case, colour,
 * alignment, and for list items whether the style is for this item or every item. Desktop keeps the bar
 * in the canvas header so it never covers the page; phones swipe it inside the docked text editor.
 * Pressing its buttons keeps the in-place editor focused. It stays after editing ends, until another
 * click on the canvas, Escape, or Close.
 */
export function TextToolbar({
  def,
  target,
  theme,
  apply,
  onClose,
  placement = "chrome",
  count,
  hint,
}: {
  /** dock: swipeable row above the phone keyboard. chrome: the bar above the canvas. */
  placement?: "dock" | "chrome";
  def: WebsiteDefinition;
  target: TextTarget;
  theme: Theme;
  apply: Apply;
  onClose: () => void;
  /** Characters used and the field's limit, shown in the chrome bar without growing it. */
  count?: { used: number; max: number };
  /** Keyboard hint, available on the count so the bar stays one row tall. */
  hint?: string;
}) {
  const docked = placement === "dock";
  const section = locateSection(def, target.sectionId)?.section;
  const generic = genericKey(target.path);
  const listItem = generic !== target.path;
  const texts = section?.style?.texts ?? {};
  // List items default to styling every item (one consistent list); "Item ini" styles just this one.
  const [scope, setScope] = useState<"item" | "all">(() => (!listItem || texts[target.path] ? "item" : "all"));
  const key = listItem && scope === "all" ? generic : target.path;
  const style = effectiveTextStyle(section, target.path);
  const own = texts[key] ?? {};

  if (!section) return null;
  const set = (patch: Partial<TextStyle>, coalesce?: string) => apply(updateTextStyle(def, target.sectionId, key, patch), coalesce && `text:${target.sectionId}:${key}:${coalesce}`);
  const size: Size = style.size ?? "md";
  const sizeAt = TEXT_SIZES.indexOf(size);
  const bump = (d: 1 | -1) => {
    const next = TEXT_SIZES[Math.min(TEXT_SIZES.length - 1, Math.max(0, sizeAt + d))]!;
    set({ size: next === "md" ? undefined : next }, "size");
  };
  const bold = (style.weight ?? 0) >= 600;
  const align = style.align ?? "start";
  const AlignIcon = ALIGN_ICONS[align];
  const color = style.color ?? theme.tokens.colors.text;
  const fontId = style.font;
  const fontName = (fontId && theme.library.fonts.find((f) => f.id === fontId)?.family.replace(/ Variable$/, "")) || t.editor.textStyle.themeFont;
  const styled = Object.keys(own).length > 0;

  // Keep the canvas editor focused while pressing toolbar buttons (popover content is portalled, so unaffected).
  const keepFocus = (e: MouseEvent) => {
    if (e.currentTarget.contains(e.target as Node) && !(e.target as HTMLElement).closest("input")) e.preventDefault();
  };

  return (
    <div
      role="toolbar"
      aria-label={t.editor.textStyle.toolbar(textLabel(target.path))}
      onMouseDown={keepFocus}
      // Touch: keep the keyboard up while tapping style buttons.
      onPointerDown={docked ? keepFocus : undefined}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "baze-text-toolbar flex items-center gap-0.5 rounded-xl bg-slate-900 p-1 text-white ring-1 ring-white/10",
        docked
          ? "w-full overflow-x-auto [scrollbar-width:none] overscroll-x-contain [&>*]:shrink-0 [&_.tb-btn]:size-10"
          : "min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] overscroll-x-contain [&>*]:shrink-0",
      )}
    >
      {docked ? null : (
        <>
          <span className={cn("max-w-28 truncate px-2 text-[11px] font-semibold tracking-wide text-white/55 uppercase", !docked && "sticky left-0 z-10 bg-slate-900")}>{textLabel(target.path)}</span>
          <Sep />
        </>
      )}
      <FontPicker theme={theme} value={fontId} weight={style.weight} label={fontName} onFont={(font) => set({ font })} onWeight={(weight) => set({ weight })} />
      <Sep />
      <Btn label={t.editor.textStyle.smaller} onClick={() => bump(-1)} disabled={sizeAt <= 0}>
        <Minus />
      </Btn>
      <span className="w-11 text-center font-mono text-[11.5px] tabular-nums text-white/85" aria-live="polite">
        {Math.round(TEXT_SIZE_SCALE[size] * 100)}%
      </span>
      <Btn label={t.editor.textStyle.bigger} onClick={() => bump(1)} disabled={sizeAt >= TEXT_SIZES.length - 1}>
        <Plus />
      </Btn>
      <Sep />
      <Btn label={t.editor.textStyle.bold} pressed={bold} onClick={() => set({ weight: bold ? 400 : 700 })}>
        <Bold />
      </Btn>
      <Btn label={t.editor.textStyle.italic} pressed={!!style.italic} onClick={() => set({ italic: style.italic ? undefined : true })}>
        <Italic />
      </Btn>
      <Btn label={t.editor.textStyle.underline} pressed={!!style.underline} onClick={() => set({ underline: style.underline ? undefined : true })}>
        <Underline />
      </Btn>
      <CasePicker value={style.case} onChange={(c) => set({ case: c })} />
      <ColorField size="dot" retainFocus label={t.editor.textStyle.color} value={color} swatches={siteSwatches(theme)} overridden={!!style.color} onReset={() => set({ color: undefined })} onChange={(hex) => set({ color: hex }, "color")} />
      <Btn label={t.editor.textStyle.align[align]} onClick={() => set({ align: ALIGN_NEXT[align] === "start" ? undefined : ALIGN_NEXT[align] })} pressed={align !== "start"}>
        <AlignIcon />
      </Btn>
      {listItem ? (
        <>
          <Sep />
          <div role="radiogroup" aria-label={t.editor.textStyle.scope} className="flex rounded-lg bg-white/10 p-0.5">
            {(["all", "item"] as const).map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={scope === s}
                onClick={() => setScope(s)}
                className="h-7 rounded-md px-2 text-[11.5px] font-semibold whitespace-nowrap text-white/70 transition hover:text-white aria-checked:bg-white aria-checked:text-slate-900"
              >
                {s === "all" ? t.editor.textStyle.allItems : t.editor.textStyle.thisItem}
              </button>
            ))}
          </div>
        </>
      ) : null}
      <Sep />
      <Btn label={t.editor.textStyle.reset} onClick={() => apply(updateTextStyle(def, target.sectionId, key, null))} disabled={!styled}>
        <RotateCcw />
      </Btn>
      {docked ? null : (
        <span className="sticky right-0 z-10 flex items-center bg-slate-900 pl-0.5">
          {count ? (
            <span className="px-1.5 font-mono text-[11px] tabular-nums text-white/55" title={hint}>
              {count.used}/{count.max}
            </span>
          ) : null}
          <Btn label={t.editor.textStyle.close} onClick={onClose}>
            <X />
          </Btn>
        </span>
      )}
    </div>
  );
}

const Sep = () => <span aria-hidden="true" className="mx-0.5 h-5 w-px bg-white/15" />;

/** Menus must not take the caret off the page text. A real text field (font search, hex) still may. */
function holdCaret(e: PointerEvent) {
  const node = e.target instanceof Element ? e.target : null;
  // A label wraps the font search and the hex field; clicking it should still focus that field.
  if (!node?.closest("input, textarea, label")) e.preventDefault();
}

/** Closing a style menu returns the caret to the in-place editor when one is open. */
function focusInlineText(e: Event) {
  const ta = document.querySelector<HTMLTextAreaElement>(".baze-inline-editor textarea, [data-docked-text]");
  if (!ta) return;
  e.preventDefault();
  ta.focus({ preventScroll: true });
}

function Btn({ label, onClick, pressed, disabled, children }: { label: string; onClick: () => void; pressed?: boolean; disabled?: boolean; children: ReactNode }) {
  return (
    <Tooltip content={label}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={pressed}
        disabled={disabled}
        onClick={onClick}
        className="tb-btn grid size-8 place-items-center rounded-lg text-white/85 transition outline-none hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/70 disabled:opacity-30 aria-pressed:bg-white aria-pressed:text-slate-900 [&_svg]:size-4"
      >
        {children}
      </button>
    </Tooltip>
  );
}

const CASES = [
  { value: undefined, label: "Aa", name: () => t.editor.textStyle.cases.none },
  { value: "uppercase", label: "AA", name: () => t.editor.textStyle.cases.uppercase },
  { value: "lowercase", label: "aa", name: () => t.editor.textStyle.cases.lowercase },
  { value: "capitalize", label: "Aa Aa", name: () => t.editor.textStyle.cases.capitalize },
] as const;

function CasePicker({ value, onChange }: { value: TextStyle["case"]; onChange: (v: TextStyle["case"]) => void }) {
  const current = value === "none" ? undefined : value;
  return (
    <Popover>
      <Tooltip content={t.editor.textStyle.case}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t.editor.textStyle.case}
            className={cn("tb-btn grid size-8 place-items-center rounded-lg text-white/85 outline-none hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/70 data-[state=open]:bg-white/15 [&_svg]:size-[18px]", current && "bg-white text-slate-900 hover:bg-white")}
          >
            <CaseSensitive />
          </button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align="center" className="w-48 p-1.5 z-[70]!" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={focusInlineText} onPointerDown={holdCaret}>
        {CASES.map((c) => (
          <button
            key={c.label}
            type="button"
            aria-pressed={current === c.value}
            onClick={() => onChange(c.value)}
            className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 aria-pressed:bg-primary-soft/60 aria-pressed:text-slate-900"
          >
            <span className="w-12 font-mono text-[13px] font-semibold">{c.label}</span>
            {c.name()}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function FontPicker({
  theme,
  value,
  weight,
  label,
  onFont,
  onWeight,
}: {
  theme: Theme;
  value: string | undefined;
  weight: number | undefined;
  label: string;
  onFont: (id: string | undefined) => void;
  onWeight: (w: number | undefined) => void;
}) {
  const [q, setQ] = useState("");
  const fonts = theme.library.fonts.filter((f) => f.family.toLowerCase().includes(q.trim().toLowerCase()));
  const groups: Array<[string, typeof fonts]> = [
    [t.editor.textStyle.fontGroups.sans, fonts.filter((f) => f.category === "sans")],
    [t.editor.textStyle.fontGroups.serif, fonts.filter((f) => f.category === "serif")],
    [t.editor.textStyle.fontGroups.other, fonts.filter((f) => f.category !== "sans" && f.category !== "serif")],
  ];
  const current = value ? theme.library.fonts.find((f) => f.id === value) : undefined;
  const preview = fontStack(current ?? theme.fonts.heading);
  const row = (id: string | undefined, name: string, family: string) => (
    <button
      key={id ?? "theme"}
      type="button"
      aria-pressed={value === id}
      onClick={() => onFont(id)}
      className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-[15px] text-slate-800 hover:bg-slate-50 aria-pressed:bg-primary-soft/60"
    >
      <span className="truncate" style={{ fontFamily: family }}>
        {name}
      </span>
    </button>
  );
  return (
    <Popover onOpenChange={(o) => !o && setQ("")}>
      <Tooltip content={t.editor.textStyle.font}>
        <PopoverTrigger asChild>
          <button type="button" aria-label={`${t.editor.textStyle.font}: ${label}`} className="flex h-8 max-w-36 items-center gap-1.5 rounded-lg px-2 [.baze-text-toolbar.w-full_&]:h-10 text-[13px] text-white/90 outline-none hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/70 data-[state=open]:bg-white/15">
            <span className="truncate" style={{ fontFamily: preview }}>
              {label}
            </span>
            <ChevronDown className="size-3.5 shrink-0 opacity-60" aria-hidden="true" />
          </button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-64 p-0 z-[70]!"
        // Phones: no keyboard over the list just for opening it; the search box is one tap away.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={focusInlineText}
        onPointerDown={holdCaret}
      >
        <div className="border-b border-line p-2">
          <label className="flex h-8 items-center gap-2 rounded-lg bg-slate-50 px-2.5 ring-1 ring-line focus-within:ring-2 focus-within:ring-primary">
            <Search className="size-3.5 text-slate-400" aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.editor.textStyle.searchFont} aria-label={t.editor.textStyle.searchFont} className="w-full bg-transparent text-sm outline-none" />
          </label>
        </div>
        <div className="scrollbar-thin max-h-64 overflow-y-auto p-1.5">
          {!q ? (
            <>
              {row(undefined, t.editor.textStyle.themeFont, fontStack(theme.fonts.heading))}
              <div className="my-1 h-px bg-line" />
            </>
          ) : null}
          {groups.map(([name, list]) =>
            list.length ? (
              <div key={name}>
                <p className="px-2.5 pt-2 pb-1 text-[10.5px] font-bold tracking-wide text-slate-400 uppercase">{name}</p>
                {list.map((f) => row(f.id, f.family.replace(/ Variable$/, ""), fontStack(f)))}
              </div>
            ) : null,
          )}
        </div>
        <div className="border-t border-line p-2">
          <p className="mb-1.5 px-0.5 text-[11px] font-semibold text-slate-500">{t.editor.textStyle.weight}</p>
          <div className="grid grid-cols-7 gap-0.5 rounded-lg bg-slate-900/5 p-0.5">
            {TEXT_WEIGHTS.map((w) => (
              <button
                key={w}
                type="button"
                aria-label={String(w)}
                aria-pressed={weight === w}
                onClick={() => onWeight(weight === w ? undefined : w)}
                className="h-7 rounded-md text-[13px] text-slate-700 hover:bg-white aria-pressed:bg-white aria-pressed:text-slate-900 aria-pressed:shadow-[0_1px_3px_rgb(15_23_42/0.12)]"
                style={{ fontFamily: preview, fontWeight: w }}
              >
                Ag
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
